import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { anime } from 'react-anime';

// eslint-disable-next-line import/no-cycle
import StyledContentBlock from '../StyledContentBlock';

import styles from './styles.module.scss';

const StyledContentCut = ({
	content,
	buttons,
	onChangeContent,
	animationDuration
}) => {
	const bodyRef = useRef(null);
	const contentRef = useRef(null);
	const [isOpen, setIsOpen] = useState(false);
	const [overflowHidden, setOverflowHidden] = useState(true);
	const [blockOpened, setBlockOpened] = useState(false);

	const changeBlockHeight = useCallback(() => {
		const height = (bodyRef.current && bodyRef.current.offsetHeight) || 0;

		if (height && contentRef.current) {
			contentRef.current.style.minHeight = `${height}px`;
		}
	}, [bodyRef.current, contentRef.current]);

	useEffect(() => {
		if (isOpen) {
			window.addEventListener('resize', changeBlockHeight);
			return () => window.removeEventListener('resize', changeBlockHeight);
		}

		return undefined;
	}, [isOpen]);

	const toggle = useCallback(
		() =>
			setIsOpen((prevState) => {
				const newIsOpen = !prevState;
				const scrollElement =
					window.document.scrollingElement ||
					window.document.documentElement ||
					window.document.body;
				const height = (bodyRef.current && bodyRef.current.offsetHeight) || 0;
				let prevContentHeight =
					(contentRef.current && contentRef.current.offsetHeight) || 0;

				const pageScrollTop = scrollElement.scrollTop;

				anime({
					targets: contentRef.current,
					minHeight: newIsOpen ? height : 0,
					duration: animationDuration,
					easing: 'easeInOutQuad',
					[newIsOpen ? 'complete' : 'begin']: () => {},
					begin: () => {
						if (newIsOpen) {
							setBlockOpened(newIsOpen);
						} else {
							setOverflowHidden(!newIsOpen);
						}
					},
					complete: () => {
						if (newIsOpen) {
							setOverflowHidden(!newIsOpen);
						} else {
							setBlockOpened(newIsOpen);
						}
					},
					update: () => {
						if (onChangeContent) {
							const newContentHeight =
								(contentRef.current && contentRef.current.offsetHeight) || 0;

							onChangeContent(newContentHeight - prevContentHeight);

							prevContentHeight = newContentHeight;
						}
					}
				});

				anime({
					targets: scrollElement,
					scrollTop: pageScrollTop - (newIsOpen ? 0 : height),
					duration: animationDuration,
					easing: 'easeInOutQuad'
				});

				return newIsOpen;
			}),
		[]
	);

	const blockCx = cx(
		cx(styles.block, {
			[styles.overflowHidden]: overflowHidden,
			[styles.open]: blockOpened
		})
	);

	return (
		<div className={blockCx}>
			<div className={styles.content} ref={contentRef}>
				<div className={styles.body} ref={bodyRef}>
					{content.map((block, index) => (
						<StyledContentBlock
							// eslint-disable-next-line react/no-array-index-key
							key={`${block.type}-${index}`}
							{...block}
						/>
					))}
				</div>
			</div>
			<button className={styles.button} onClick={toggle} type="button">
				{blockOpened ? buttons.fold : buttons.expand}
			</button>
		</div>
	);
};

StyledContentCut.propTypes = {
	content: PropTypes.arrayOf(
		PropTypes.shape({ type: PropTypes.string.isRequired }).isRequired
	).isRequired,
	buttons: PropTypes.shape({ expand: PropTypes.string, fold: PropTypes.string })
		.isRequired,
	onChangeContent: PropTypes.func,
	animationDuration: PropTypes.number
};

StyledContentCut.defaultProps = {
	animationDuration: 150
};

export default StyledContentCut;
